import { useEffect, useState } from "react";
import { Modal, message } from "antd";
import { Eye, Download, Loader2 } from "lucide-react";
import axios from "../../axios/AxiosInstance";
import PageHeading from "../../components/PageHeading";
import ClientTabHeading from "../clients/ClientTabHeading";
import { apiErrorMessage } from "../../utils/apiError";

export function matchesRegisterClient(row, client) {
  if (!client) return true;
  if (row.clientId)
    return String(row.clientId?._id || row.clientId) === String(client._id);
  if (row.clientCode && client.pld != null)
    return String(row.clientCode) === String(client.pld);
  return Boolean(client.companyName) && row.client === client.companyName;
}
export default function RegistrePersonnel({ client, embedded = false }) {
  const [rows, setRows] = useState([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [search, setSearch] = useState(""),
    [month, setMonth] = useState(""),
    [busy, setBusy] = useState(""),
    [preview, setPreview] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    axios
      .get("registre/freelance", { signal: controller.signal })
      .then(({ data }) => {
        if (!Array.isArray(data)) throw Error("Réponse invalide");
        setRows(data);
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(
            apiErrorMessage(
              e,
              "Impossible de charger les registres de l’agence.",
            ),
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview],
  );
  const open = async (row, download) => {
    if (busy) return;
    setBusy(row._id);
    try {
      const { data } = await axios.get(`registre/freelance/${row._id}`);
      const base64 = String(data?.data || "").replace(/^data:[^,]+,/, "");
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      if (String.fromCharCode(...bytes.slice(0, 5)) !== "%PDF-")
        throw Error("PDF absent");
      const url = URL.createObjectURL(
        new Blob([bytes], { type: "application/pdf" }),
      );
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download =
          row.fileName || `registre_${row.monthKey || row._id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } else setPreview({ url, title: `${row.client} · ${row.month}` });
    } catch (e) {
      message.error(
        apiErrorMessage(
          e,
          "Le registre original est indisponible. Contactez l’agence.",
        ),
      );
    } finally {
      setBusy("");
    }
  };
  const filtered = rows.filter(
    (row) =>
      matchesRegisterClient(row, client) &&
      (!month || row.monthKey === month) &&
      `${row.client} ${row.month}`
        .toLocaleLowerCase("fr")
        .includes(search.toLocaleLowerCase("fr")),
  );
  return (
    <div className="space-y-5">
      {embedded ? (
        <ClientTabHeading
          title="Registre du personnel"
          description="Registres originaux transmis par l’agence."
        />
      ) : (
        <PageHeading
          icon="register"
          title="Registre du personnel"
          description="Consultez les documents originaux importés par l’agence pour vos clients."
        />
      )}
      <p className="text-sm text-slate-500">
        Seuls les registres transmis par l’agence sont affichés. Aucun document
        n’est reconstruit à partir des contrats.
      </p>
      <div className="flex flex-wrap gap-4">
        <label>
          Rechercher un client
          <input
            className="block rounded-lg border p-2"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label>
          Mois du registre
          <input
            type="month"
            className="block rounded-lg border p-2"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </label>
        {month && <button onClick={() => setMonth("")}>Tous les mois</button>}
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? (
        <p role="status">
          <Loader2 className="inline animate-spin" /> Chargement des registres…
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th>Client</th>
                <th>Période</th>
                <th>Document</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row._id}>
                  <td>{row.client}</td>
                  <td>{row.month || row.monthKey}</td>
                  <td>{row.fileName || "Registre importé"}</td>
                  <td>
                    <div className="flex gap-3">
                      <button
                        disabled={!!busy}
                        onClick={() => open(row, false)}
                        aria-label={`Consulter le registre ${row.client} ${row.month}`}
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        disabled={!!busy}
                        onClick={() => open(row, true)}
                        aria-label={`Télécharger le registre ${row.client} ${row.month}`}
                      >
                        <Download size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-slate-500">
                    Aucun registre transmis par l’agence pour cette sélection.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        open={!!preview}
        title={preview?.title}
        width={1000}
        onCancel={() => setPreview(null)}
        footer={null}
      >
        {preview && (
          <iframe
            title="Registre original de l’agence"
            src={preview.url}
            className="h-[70vh] w-full"
          />
        )}
      </Modal>
    </div>
  );
}
